import { LocalizedStringFieldModel, StringFieldModel } from "@/common/commonForms";
import { Language, TranslationDirection } from "@/i18n/languages";
import { TMessage } from "@/i18n/tt";
import { makeAutoObservable } from "mobx";
import { isOk, LocalizedText, TranslationOption } from "@/common/common";
import { isNotBlank } from "@/utils/strings";
import { AIClient, AIModel } from "@/ai/AIAssistant";
import { SelectionAwareDataSource } from "@/ai/autotext";

export type LocalizedTextEditorModelProps = {
  aiClient: AIClient;
  texts?: LocalizedText[];
  isReadOnly: () => boolean;
  required?: boolean;
  onChange?: (translationOption: TranslationOption, language: Language, value: string) => void;
  onAutoTranslationDisabledByChange?: () => void;
  label: TMessage;
  dataProvider?: SelectionAwareDataSource;
};

export class LocalizedTextEditorModel {
  textModels: LocalizedStringFieldModel[];
  translationDirection?: TranslationDirection;
  autoTranslate: boolean;
  label: TMessage;
  params: LocalizedTextEditorModelProps;
  dataProvider?: SelectionAwareDataSource;

  constructor(params: LocalizedTextEditorModelProps) {
    const texts = params.texts ?? [];
    this.params = params;
    this.dataProvider = params.dataProvider;
    this.autoTranslate = texts[0]?.translationOption?.autoTranslate ?? true;
    this.translationDirection =
      texts[0]?.translationOption?.translationDirection ??
      (texts.length > 1 ? { from: texts[0].language, to: texts[1].language } : undefined);
    this.textModels = texts.map((x) => this.initTextModel(x));
    this.label = params.label;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  initTextModel({ text, language }: LocalizedText): LocalizedStringFieldModel {
    return new LocalizedStringFieldModel(
      new StringFieldModel({
        value: text,
        required: this.params.required,
        onChange: (value) => {
          this.params.onChange?.(this.translationOption, language, value ?? "");
          this.checkTriggerTranslation({ language, text: value ?? "" });
        },
      }),
      language,
      new AIModel(this.params.aiClient, this.dataProvider),
    );
  }

  toggleAutoTranslate() {
    this.setTranslationOption({
      translationDirection: this.translationDirection,
      autoTranslate: !this.autoTranslate,
    });
  }

  toggleTranslationDirection() {
    if (this.translationDirection) {
      this.setTranslationOption({
        autoTranslate: this.autoTranslate,
        translationDirection: {
          from: this.translationDirection.to,
          to: this.translationDirection.from,
        },
      });
    }
  }

  private notifyChange() {
    this.textModels.forEach((t) => {
      this.params.onChange?.(this.translationOption, t.locale, t.model.inputValue);
    });
  }

  get translationOption(): TranslationOption {
    return {
      autoTranslate: this.autoTranslate,
      translationDirection: this.translationDirection,
    };
  }

  setTranslationOption(translationOption: TranslationOption, notifyChange = true) {
    this.autoTranslate = translationOption.autoTranslate;
    this.translationDirection = translationOption.translationDirection;
    if (notifyChange) this.notifyChange();
    if (this.autoTranslate) {
      const src = this.textModels.find((t) => t.locale === this.translationDirection?.from);
      const target = this.textModels.find((t) => t.locale === this.translationDirection?.to);
      if (src && target && target.model.isEmpty) {
        this.checkTriggerTranslation(src);
      }
    }
  }

  get localizedTexts(): LocalizedText[] {
    return this.textModels.map((t) => {
      return {
        language: t.locale,
        text: t.model.validatedValue,
        translationOption: this.translationOption,
      };
    });
  }

  applyImproveMessageResult(localized: LocalizedStringFieldModel) {
    const aiAssistant = localized.aiAssistant!;
    if (isOk(aiAssistant.improveMessageResult)) {
      localized.model.setAndCommitInputValue(aiAssistant.improveMessageResult.data);
      aiAssistant.improveMessageResult = undefined;
      this.checkTriggerTranslation(localized);
    }
  }

  checkTriggerTranslation({ text, language }: LocalizedText) {
    if (
      this.translationDirection?.from === language &&
      this.textModels.length > 1 &&
      this.autoTranslate
    ) {
      this.translate(text);
    }
  }

  translate(value: string) {
    const localized = this.textModels.find((t) => t.locale === this.translationDirection?.to);
    if (localized !== undefined && this.translationDirection !== undefined) {
      localized.aiAssistant!.translateMessage(
        value,
        this.translationDirection.from,
        localized.locale,
        (result: string) => localized.model.setAndCommitInputValue(result),
      );
    }
  }

  isReadOnly(localized: LocalizedStringFieldModel): boolean | undefined {
    return this.params.isReadOnly() || localized.aiAssistant?.showImproveMessageProgressBar;
  }

  onLanguageChange(
    languages: Language[] | undefined,
    initialData: LocalizedText[] | undefined,
    translationOption?: TranslationOption,
  ) {
    this.textModels =
      languages?.map((language) => {
        const localized = this.textModels.find((t) => t.locale === language);
        if (localized) {
          return localized;
        }
        const initial = initialData?.find((t) => t.language === language) ?? initialData?.[0];
        return this.initTextModel({ text: initial?.text ?? "", language });
      }) ?? [];

    if (translationOption) {
      this.autoTranslate = translationOption.autoTranslate;
      this.translationDirection = translationOption.translationDirection;
    } else if (this.textModels.length > 1 && this.translationDirection === undefined) {
      this.translationDirection = {
        from: this.textModels[0].locale,
        to: this.textModels[1].locale,
      };
    }

    const to = this.translationDirection?.to;
    const alreadyTranslated = initialData?.some((t) => t.language === to && isNotBlank(t.text));
    if (this.textModels[0] && !alreadyTranslated) {
      this.checkTriggerTranslation(this.textModels[0]);
    }
  }

  get isEmpty(): boolean {
    return this.textModels.every((m) => m.model.isEmpty);
  }

  get isValid(): boolean {
    return this.textModels.every((m) => m.model.isValid);
  }

  validate(): boolean {
    this.textModels.forEach((m) => {
      m.model.validate();
    });
    return this.isValid;
  }

  disableAutoTranslate(localized: LocalizedStringFieldModel) {
    if (this.translationDirection?.to === localized.locale && this.autoTranslate) {
      this.toggleAutoTranslate();
      return true;
    }
  }
}
